import "shared/ui/toolbar/tools/layertreeAndEdit/layerContent/layerContent.css";

import {
  Box,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { defaultStore } from "shared/common/stores/defaultStore";
import {
  sidebarStore,
  WebServiceLayerType,
} from "shared/common/stores/sidebarStore";
import ArcgisOptions from "./webServicesOptions/ArcgisOptions";
import GeoserverOptions from "./webServicesOptions/GeoserverOption";
import QgisOptions from "./webServicesOptions/QgisOption";
import Wfs20Options from "./webServicesOptions/Wfs20Option";
import { getLayersAddedByUser } from "../../../../../../common/utils/layers/localStorageLayers";

export default function AddWebServiceLayer() {
  const darkTheme = defaultStore((state) => state.darkTheme);
  const layerConfiguration = defaultStore((state) => state.layerConfiguration);

  const webServiceLayer = sidebarStore((state) => state.webServiceLayer);
  const changeWebServiceLayer = sidebarStore(
    (state) => state.changeWebServiceLayer
  );
  const changeSaveButtonStateOnAddNewLayers = sidebarStore(
    (state) => state.changeSaveButtonStateOnAddNewLayers
  );

  const { t } = useTranslation();

  const [oldConnections, setOldConnections] = useState<WebServiceLayerType[]>(
    []
  );
  const [connectionSelected, setConnectionSelected] =
    useState<WebServiceLayerType>();

  const [useOldConnection, setUseOldConnection] = useState<boolean>(false);
  const [layerType, setLayerType] = useState<string>("");
  const [serviceUrl, setServiceUrl] = useState<string>("");
  const [validURL, setValidURL] = useState<boolean>(true);

  //check if can save to enable save button
  const checkIfCanSave = () => {
    if (
      webServiceLayer.url !== "" &&
      webServiceLayer.url !== undefined &&
      webServiceLayer.name !== "" &&
      webServiceLayer.name !== undefined &&
      webServiceLayer.type !== "" &&
      webServiceLayer.type !== undefined &&
      validURL
    ) {
      changeSaveButtonStateOnAddNewLayers(true);
    } else {
      changeSaveButtonStateOnAddNewLayers(false);
    }
  };

  //check if its a valid url
  const isValidURL = (url: string) => {
    if (url !== "") {
      setValidURL(
        /^(?:(?:https?|ftp):\/\/)?(?:www\.)?[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]{2,})+(?:\/[^\s]*)?$/i.test(
          url
        )
      );
    } else {
      setValidURL(true);
    }
  };

  //if its not valid url disable save button
  useEffect(() => {
    if (!validURL) {
      changeSaveButtonStateOnAddNewLayers(false);
    } else {
      checkIfCanSave();
    }
  }, [validURL]);

  //if selected old connection fill webserviceslayer state with datas
  useEffect(() => {
    if (connectionSelected) {
      setUseOldConnection(true);
      const webService: WebServiceLayerType = {
        source_options: connectionSelected.source_options,
        topic: connectionSelected.topic,
        type: connectionSelected.type,
        name: connectionSelected.name,
        url: connectionSelected.url,
        project: connectionSelected.project,
        layers: connectionSelected.layers,
      };
      changeWebServiceLayer(webService);

      setLayerType(connectionSelected.type || "");

      let url: string | undefined = connectionSelected.url;
      if (url && connectionSelected.project)
        url = concatenateUrl(url, connectionSelected.project);
      if (url && connectionSelected.source_options?.servertype)
        url = concatenateUrl(url, connectionSelected.source_options.servertype);
      setServiceUrl(url ? url : "");
    } else {
      setUseOldConnection(false);
      resetConnectionDatas();
    }
  }, [connectionSelected]);

  function concatenateUrl(base: string, path: string) {
    return base.replace(/\/$/, "") + "/" + path.replace(/^\//, "");
  }

  // fill project name and server type from based on url from users
  const fillProjectAndServerTypeFromURL = (url: string, type: string) => {
    if (url !== "" && type !== "") {
      const urlAttributes = url.split("?");
      const urlLinkAndPages = urlAttributes[0];
      const urlParams = urlAttributes[1] || "";

      // TODO: Add check, if url is containing a valid project name (and server type)
      if (type === "qgiswfs") {
        //QGIS project name is saved on urlMapParam
        const urlMapParam = urlParams.toLowerCase().split("&map=");
        webServiceLayer.url = urlLinkAndPages;
        webServiceLayer.project = urlMapParam[1] || "";
        delete webServiceLayer.source_options;
      } else if (type === "arcgis") {
        const pages = urlLinkAndPages.split("/");
        let projectName = undefined;
        let serverType = undefined;
        pages.forEach((item, index) => {
          if (
            item.toLowerCase() === "featureserver" ||
            item.toLowerCase() === "mapserver"
          ) {
            projectName = pages[index - 1];
            if (item.toLowerCase() === "featureserver") {
              serverType = "FeatureServer";
            } else {
              serverType = "MapServer";
            }
          }
        });
        if (!projectName) {
          projectName = pages[pages.length - 1];
        }
        webServiceLayer.url = urlLinkAndPages.split(projectName)[0];
        webServiceLayer.project = projectName;
        if (serverType) {
          if (webServiceLayer.source_options) {
            webServiceLayer.source_options.servertype = serverType;
          } else {
            webServiceLayer.source_options = {
              servertype: serverType,
            };
          }
        }
      } else if (type === "wfs") {
        const pageLink = urlLinkAndPages.split("/wfs");
        const pages = (pageLink[0] || "").split("/");
        const projectName = pages[pages.length - 1];

        if (projectName && projectName !== "") {
          webServiceLayer.url = pageLink[0].split(projectName)[0];
        } else {
          webServiceLayer.url = pageLink[0];
        }
        //NOTE:: project name should not includes a point in the name
        if (projectName.includes(".")) {
          delete webServiceLayer.project;
          delete webServiceLayer.source_options;
        } else {
          webServiceLayer.project = projectName;
          delete webServiceLayer.source_options;
        }
      } else {
        delete webServiceLayer.project;
        delete webServiceLayer.source_options;
      }
    } else if (url === "" && type === "") {
      delete webServiceLayer.project;
      delete webServiceLayer.source_options;
      delete webServiceLayer.url;
    }
  };

  //reset connection datas
  const resetConnectionDatas = () => {
    setLayerType("");
    setServiceUrl("");
    changeWebServiceLayer({});
  };

  //if use an old connection check if can save
  useEffect(() => {
    checkIfCanSave();
  }, [useOldConnection]);

  useEffect(() => {
    const connections: WebServiceLayerType[] = [];
    const connectionNames: string[] = [];
    if (layerConfiguration) {
      layerConfiguration
        .filter(
          (item: any) =>
            ["arcgis", "wfs", "qgiswfs", "wfs20"].includes(item.type) &&
            item?.project !== undefined
        )
        .forEach((conn: WebServiceLayerType, index: number) => {
          if (!conn.name) {
            if (!connectionNames.includes(`${conn.url} - ${conn.project}`)) {
              connectionNames.push(`${conn.url} - ${conn.project}`);
              conn.name = `${conn.url} - ${conn.project}`;
              connections.push(conn);
            }
          } else {
            if (!connectionNames.includes(conn.name)) {
              connectionNames.push(conn.name);
              connections.push(conn);
            }
          }
        });
    }
    // add layers configurations from local storage
    const allLayersFromUser = getLayersAddedByUser();
    if (allLayersFromUser.length > 0) {
      allLayersFromUser
        .filter((item: any) => {
          if (item?.type === "wfs20") return true; // WFS 2.0 does not have a project
          return (
            ["arcgis", "wfs", "qgiswfs"].includes(item.type) &&
            item?.project !== undefined
          );
        })
        .forEach((conn: any, index: number) => {
          if (!conn.name) {
            if (!connectionNames.includes(`${conn.url} - ${conn.project}`)) {
              connectionNames.push(`${conn.url} - ${conn.project}`);
              conn.name = `${conn.url} - ${conn.project}`;
              connections.push(conn);
            }
          } else {
            if (!connectionNames.includes(conn.name)) {
              connectionNames.push(conn.name);
              connections.push(conn);
            }
          }
        });
    }
    setOldConnections(connections);
  }, [layerConfiguration]);

  //set default state-s for different types
  const changeOptions = (type: string) => {
    switch (type) {
      case "arcgis":
        webServiceLayer.source_options = {
          servertype: "FeatureServer",
        };
        break;
      case "wfs":
        webServiceLayer.source_options = {
          withCredentials: true,
        };
        break;
      case "qgiswfs":
        webServiceLayer.source_options = {
          withCredentials: true,
        };
        break;
      case "wfs20":
        webServiceLayer.source_options = {};
        break;
      default:
        webServiceLayer.source_options = {};
    }
  };

  return (
    <Box
      display={"flex"}
      flexDirection={"column"}
      width={"100%"}
      height={"calc(100% - 16px)"}
      overflow={"auto"}
      paddingY={1}
      gap={3}
    >
      <FormControl
        size="small"
        sx={{
          width: { xs: "100%", md: "calc(60% - 8px)" },
        }}
      >
        <InputLabel>{t("add_layer.connection")}</InputLabel>
        <Select
          label="Connection"
          value={connectionSelected?.name ? connectionSelected.name : ""}
          onChange={(event) => {
            setConnectionSelected(
              oldConnections.find((item) => item.name === event.target.value)
            );
          }}
        >
          <MenuItem key="selectConnection" id="selectConnection" value={""}>
            {t("add_layer.connection_selection")}
          </MenuItem>
          {oldConnections.map((item, index) => (
            <MenuItem
              key={`${index}_${item.name}`}
              id={`${index}_${item.name}`}
              value={item.name}
            >
              {item.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Divider />
      <Box display={"flex"} flexDirection={"column"} gap={2}>
        {!validURL && (
          <Typography color={"red"} fontSize={"14px"} fontStyle={"italic"}>
            Please write valid URL
          </Typography>
        )}
        <TextField
          label={t("add_layer.url")}
          variant="outlined"
          value={serviceUrl}
          size="small"
          fullWidth
          onChange={(event) => {
            isValidURL(event.target.value);
            setServiceUrl(event.target.value);
            webServiceLayer["url"] = event.target.value;
            fillProjectAndServerTypeFromURL(
              event.target.value,
              useOldConnection && webServiceLayer?.type
                ? webServiceLayer.type
                : layerType
            );
            checkIfCanSave();
          }}
        />
        <Box
          display={"flex"}
          sx={{ flexDirection: { xs: "column", md: "row" } }}
          gap={2}
          alignItems={"center"}
        >
          <TextField
            label={t("add_layer.connection_name")}
            variant="outlined"
            size="small"
            sx={{
              width: { xs: "100%", md: "calc(60% - 8px)" },
            }}
            onChange={(event) => {
              webServiceLayer.name = event.target.value;
              checkIfCanSave();
            }}
          />
          <FormControl
            size="small"
            sx={{
              width: { xs: "100%", md: "calc(40% - 8px)" },
            }}
          >
            <InputLabel>{t("add_layer.connection_type")}</InputLabel>
            <Select
              label="Type"
              value={
                useOldConnection && webServiceLayer?.type
                  ? webServiceLayer.type
                  : layerType
              }
              onChange={(event) => {
                setLayerType(event.target.value);
                changeOptions(event.target.value);
                webServiceLayer["type"] = event.target.value;
                fillProjectAndServerTypeFromURL(
                  useOldConnection &&
                    webServiceLayer?.type &&
                    webServiceLayer?.url
                    ? webServiceLayer.url
                    : serviceUrl,
                  event.target.value
                );
                checkIfCanSave();
              }}
            >
              <MenuItem key="arcgis" id="arcgis" value={"arcgis"}>
                {t("add_layer.connection_type_1")}
              </MenuItem>
              <MenuItem key="wfs" id="wfs" value={"wfs"}>
                {t("add_layer.connection_type_2")}
              </MenuItem>
              <MenuItem key="qgiswfs" id="qgiswfs" value={"qgiswfs"}>
                {t("add_layer.connection_type_3")}
              </MenuItem>
              <MenuItem key="wfs20" id="wfs20" value={"wfs20"}>
                {t("add_layer.connection_type_4")}
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>
      {layerType !== "" && (
        <Box display={"flex"} flexDirection={"column"} gap={2}>
          <Typography
            fontWeight={600}
            fontSize={20}
            color={darkTheme ? "white" : "darkblue.main"}
          >
            {t("add_layer.options")}
          </Typography>
          {layerType === "wfs" && (
            <GeoserverOptions
              webServiceLayer={webServiceLayer}
              useOldConnection={useOldConnection}
            />
          )}
          {layerType === "qgiswfs" && (
            <QgisOptions
              webServiceLayer={webServiceLayer}
              useOldConnection={useOldConnection}
            />
          )}
          {layerType === "wfs20" && (
            <Wfs20Options
              webServiceLayer={webServiceLayer}
              useOldConnection={useOldConnection}
            />
          )}
          {layerType === "arcgis" && (
            <ArcgisOptions
              webServiceLayer={webServiceLayer}
              useOldConnection={useOldConnection}
            />
          )}
        </Box>
      )}
    </Box>
  );
}
